import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../common/Button';
import Modal from '../common/Modal';
import Input from '../common/Input';
import { ROUTES } from '../../utils/constants';

export default function FriDaveHeader() {
  const { isAuthenticated, login, logout } = useAuth();
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    const result = await login(password);
    if (result.success) {
      setShowLoginModal(false);
      setPassword('');
    } else {
      setError(result.error || 'Invalid password');
    }
    setIsLoading(false);
  };

  const handleLogout = () => {
    logout();
    setShowMenu(false);
  };

  const handleNavigate = (path) => {
    navigate(path);
    setShowMenu(false);
  };

  return (
    <>
      <header className="bg-white border-b border-gray-200 relative z-50">
        <div className="w-full px-4 py-3">
          <div className="flex items-center justify-center relative">
            {/* Logo */}
            <button
              onClick={() => navigate(ROUTES.FRIDAVE_HOME)}
              className="hover:opacity-70 transition-opacity px-2 py-1"
            >
              <div style={{ fontStyle: 'italic', fontWeight: 900, fontSize: '22px', background: 'linear-gradient(to bottom, #C9900A, #7A4F00)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', lineHeight: 1.1 }}>FriDave's</div>
              <div style={{ fontStyle: 'italic', fontWeight: 900, fontSize: '18px', background: 'linear-gradient(to bottom, #C9900A, #7A4F00)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', lineHeight: 1.1 }}>Super Series</div>
            </button>

            {/* Hamburger */}
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="absolute right-0 w-12 h-12 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Menu"
            >
              <div className="w-6 h-5 flex flex-col justify-between">
                <span className={`block h-0.5 w-full bg-gray-700 transition-all ${showMenu ? 'rotate-45 translate-y-2' : ''}`}></span>
                <span className={`block h-0.5 w-full bg-gray-700 transition-all ${showMenu ? 'opacity-0' : ''}`}></span>
                <span className={`block h-0.5 w-full bg-gray-700 transition-all ${showMenu ? '-rotate-45 -translate-y-2' : ''}`}></span>
              </div>
            </button>
          </div>
        </div>
      </header>

      {/* Sliding Drawer */}
      <div className={`fixed inset-0 z-40 transition-opacity ${showMenu ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
        <div className="absolute inset-0 bg-black/60" onClick={() => setShowMenu(false)}></div>
        <div className={`absolute top-0 right-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl transform transition-transform ${showMenu ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="px-4 py-5 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Admin Menu</h2>
            <button onClick={() => setShowMenu(false)} className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex flex-col h-[calc(100%-80px)]">
            {isAuthenticated ? (
              <>
                <div className="flex-1 overflow-y-auto p-4 space-y-1">
                  <button
                    onClick={() => handleNavigate(ROUTES.FRIDAVE_ROUNDS)}
                    className="w-full flex items-center gap-4 px-4 py-4 text-left text-gray-800 hover:bg-gray-50 rounded-xl min-h-touch-lg"
                  >
                    <span className="text-2xl">🏆</span>
                    <span className="text-base font-semibold">Manage Events</span>
                  </button>
                  <button
                    onClick={() => handleNavigate(ROUTES.PLAYERS)}
                    className="w-full flex items-center gap-4 px-4 py-4 text-left text-gray-800 hover:bg-gray-50 rounded-xl min-h-touch-lg"
                  >
                    <span className="text-2xl">👥</span>
                    <span className="text-base font-semibold">Manage Players</span>
                  </button>
                </div>
                <div className="p-4 border-t border-gray-200">
                  <button
                    onClick={handleLogout}
                    className="w-full py-3 rounded-xl border border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 transition-colors"
                  >
                    Logout
                  </button>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center p-6">
                <div className="text-center">
                  <div className="text-6xl mb-4">🔒</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Admin Access</h3>
                  <p className="text-gray-400 mb-6 text-sm">Log in to manage events</p>
                  <button
                    onClick={() => { setShowMenu(false); setShowLoginModal(true); }}
                    className="px-6 py-3 bg-fd-primary text-white font-bold rounded-xl hover:bg-fd-secondary transition-colors"
                  >
                    Admin Login
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Login Modal */}
      <Modal isOpen={showLoginModal} onClose={() => { setShowLoginModal(false); setPassword(''); setError(''); }} title="Admin Login" size="sm">
        <form onSubmit={handleLogin}>
          <Input label="Admin Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} error={error} placeholder="Enter admin password" required autoFocus />
          <div className="flex gap-3 mt-6">
            <Button type="button" variant="secondary" onClick={() => { setShowLoginModal(false); setPassword(''); setError(''); }} className="flex-1">Cancel</Button>
            <Button type="submit" variant="primary" disabled={!password || isLoading} className="flex-1">{isLoading ? 'Logging in...' : 'Login'}</Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
